/**
 * Scouting Report Modal
 * Form for submitting intel on opponents with autocomplete
 * 🔒 TOP SECRET - Peck Pirates Intel Database
 */

import { useState, useEffect, useRef } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, addDoc, serverTimestamp, query, orderBy, limit } from 'firebase/firestore';
import type { ScoutingReportDocument } from '@schemas/firestore';

// Game options
const GAMES = [
    { id: 'smash-bros-solo', name: 'Smash Bros Solo' },
    { id: 'smash-bros-crews', name: 'Smash Bros Crews' },
    { id: 'rocket-league', name: 'Rocket League' },
    { id: 'mario-kart', name: 'Mario Kart' },
    { id: 'madden', name: 'Madden NFL' },
    { id: 'fortnite', name: 'Fortnite' },
];

interface ScoutingReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventId?: string;
    eventDate?: string;
    eventTitle?: string;
    userId: string;
    userName: string;
    onSuccess?: () => void;
}

interface ExistingOpponent {
    name: string;
    team?: string;
    game: string;
    count: number;
    stats?: { wins: number; losses: number };
}

export default function ScoutingReportModal({
    isOpen,
    onClose,
    eventId,
    eventDate,
    eventTitle,
    userId,
    userName,
    onSuccess,
}: ScoutingReportModalProps) {
    const [opponentName, setOpponentName] = useState('');
    const [opponentTeam, setOpponentTeam] = useState('');
    const [opponentCharacter, setOpponentCharacter] = useState('');
    const [game, setGame] = useState(GAMES[0].id);
    const [strategies, setStrategies] = useState('');
    const [weaknesses, setWeaknesses] = useState('');
    const [strengths, setStrengths] = useState('');
    const [notes, setNotes] = useState('');
    const [playerNames, setPlayerNames] = useState('');
    const [vodUrl, setVodUrl] = useState('');
    const [startGGMatchUrl, setStartGGMatchUrl] = useState('');
    const [tags, setTags] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [existingOpponents, setExistingOpponents] = useState<ExistingOpponent[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [error, setError] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);

    // Load existing opponents for autocomplete (from both scouting reports and imported opponents)
    useEffect(() => {
        async function loadOpponents() {
            try {
                const opponentMap = new Map<string, ExistingOpponent>();

                // 1. Load from existing scouting reports
                const reportsSnap = await getDocs(
                    query(collection(db, 'scoutingReports'), orderBy('createdAt', 'desc'), limit(100))
                );

                reportsSnap.docs.forEach(doc => {
                    const data = doc.data() as ScoutingReportDocument;
                    const key = `${data.opponentName}-${data.game}`;

                    if (opponentMap.has(key)) {
                        opponentMap.get(key)!.count++;
                    } else {
                        opponentMap.set(key, {
                            name: data.opponentName,
                            team: data.opponentTeam,
                            game: data.game,
                            count: 1,
                        });
                    }
                });

                // 2. Load from imported opponents database (LeagueOS)
                try {
                    const importedSnap = await getDocs(collection(db, 'opponents'));
                    importedSnap.docs.forEach(doc => {
                        const data = doc.data();
                        const schoolName = data.name as string;
                        const formats = (data.formats || []) as string[];
                        const wins = data.totalWins as number || 0;
                        const losses = data.totalLosses as number || 0;

                        // Map formats to game IDs
                        formats.forEach(format => {
                            let gameId = 'smash-bros-solo';
                            if (format === '3crewbattles') gameId = 'smash-bros-crews';
                            else if (format === 'solos') gameId = 'smash-bros-solo';

                            const key = `${schoolName}-${gameId}`;
                            if (!opponentMap.has(key)) {
                                opponentMap.set(key, {
                                    name: schoolName,
                                    team: data.tag || undefined,
                                    game: gameId,
                                    count: 0, // 0 = from import, not scouting reports
                                    stats: { wins, losses },
                                });
                            }
                        });
                    });
                } catch (err) {
                    // Imported opponents not available yet - that's ok
                    console.debug('No imported opponents collection yet');
                }

                setExistingOpponents(Array.from(opponentMap.values()));
            } catch (err) {
                console.error('Error loading opponents:', err);
            }
        }

        if (isOpen) {
            loadOpponents();
        }
    }, [isOpen]);

    // Filter suggestions
    const filteredSuggestions = existingOpponents.filter(o =>
        o.name.toLowerCase().includes(opponentName.toLowerCase()) ||
        (o.team && o.team.toLowerCase().includes(opponentName.toLowerCase()))
    ).slice(0, 5);

    // Handle suggestion selection
    const selectSuggestion = (opponent: ExistingOpponent) => {
        setOpponentName(opponent.name);
        setOpponentTeam(opponent.team || '');
        setGame(opponent.game);
        // We don't autocomplete character from opponent history yet as it varies per match
        setShowSuggestions(false);
    };

    // Submit report
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!opponentName.trim() || !strategies.trim()) {
            setError('Opponent name and strategies are required');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const playerNamesArray = playerNames
                .split(',')
                .map(p => p.trim())
                .filter(p => p.length > 0);

            const tagsArray = tags
                .split(',')
                .map(t => t.trim().toLowerCase())
                .filter(t => t.length > 0);

            await addDoc(collection(db, 'scoutingReports'), {
                opponentName: opponentName.trim(),
                opponentTeam: opponentTeam.trim() || null,
                opponentCharacter: opponentCharacter.trim() || null,
                game,
                strategies: strategies.trim(),
                weaknesses: weaknesses.trim() || null,
                strengths: strengths.trim() || null,
                notes: notes.trim() || null,
                eventId: eventId || null,
                eventDate: eventDate || null,
                vodUrl: vodUrl.trim() || null,
                startGGMatchUrl: startGGMatchUrl.trim() || null,
                playerNames: playerNamesArray,
                tags: tagsArray,
                createdBy: userId,
                createdByName: userName,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            // Reset form
            setOpponentName('');
            setOpponentTeam('');
            setOpponentCharacter('');
            setStrategies('');
            setWeaknesses('');
            setStrengths('');
            setNotes('');
            setPlayerNames('');
            setVodUrl('');
            setStartGGMatchUrl('');
            setTags('');

            onSuccess?.();
            onClose();
        } catch (err) {
            console.error('Error submitting report:', err);
            setError('Failed to submit report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-secondary-900 rounded-xl border border-secondary-700 shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-red-900/60 via-secondary-900 to-red-900/60 border-b border-red-500/30 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">🔒</span>
                            <div>
                                <h2 className="text-lg font-bold text-white">New Scouting Report</h2>
                                <p className="text-xs text-red-400 uppercase tracking-wider">
                                    Top Secret Intel Submission
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-secondary-400 hover:text-white hover:bg-secondary-800 rounded-lg transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    {eventTitle && (
                        <p className="text-sm text-secondary-400 mt-2">
                            📅 Related Event: {eventTitle}
                        </p>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {error && (
                        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Opponent Info Grid */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="relative sm:col-span-2">
                            <label className="block text-base sm:text-sm font-medium text-secondary-300 mb-1">
                                Opponent Name *
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                value={opponentName}
                                onChange={(e) => {
                                    setOpponentName(e.target.value);
                                    setShowSuggestions(true);
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                placeholder="Player or team name"
                                className="w-full px-4 py-3 sm:px-3 sm:py-2 bg-secondary-800 border border-secondary-600 rounded-xl sm:rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 text-base"
                                required
                            />

                            {/* Autocomplete Suggestions */}
                            {showSuggestions && filteredSuggestions.length > 0 && opponentName.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-secondary-800 border border-secondary-600 rounded-lg shadow-xl overflow-hidden">
                                    {filteredSuggestions.map((opponent, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => selectSuggestion(opponent)}
                                            className="w-full px-4 py-3 text-left hover:bg-secondary-700 transition-colors border-b border-secondary-700 last:border-0"
                                        >
                                            <p className="text-white text-base font-medium">
                                                {opponent.name}
                                                {opponent.team && (
                                                    <span className="text-secondary-400 ml-2">
                                                        ({opponent.team})
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-sm text-secondary-500">
                                                {GAMES.find(g => g.id === opponent.game)?.name || opponent.game}
                                                {opponent.stats && (
                                                    <span className="ml-2">
                                                        <span className="text-green-400">{opponent.stats.wins}W</span>
                                                        <span className="mx-0.5">-</span>
                                                        <span className="text-red-400">{opponent.stats.losses}L</span>
                                                    </span>
                                                )}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-base sm:text-sm font-medium text-secondary-300 mb-1">
                                Team/School
                            </label>
                            <input
                                type="text"
                                value={opponentTeam}
                                onChange={(e) => setOpponentTeam(e.target.value)}
                                placeholder="Optional team name"
                                className="w-full px-4 py-3 sm:px-3 sm:py-2 bg-secondary-800 border border-secondary-600 rounded-xl sm:rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 text-base"
                            />
                        </div>

                        <div>
                            <label className="block text-base sm:text-sm font-medium text-secondary-300 mb-1">
                                Character / Fighter
                            </label>
                            <input
                                type="text"
                                value={opponentCharacter}
                                onChange={(e) => setOpponentCharacter(e.target.value)}
                                placeholder="e.g. Mario, Lucina"
                                className="w-full px-4 py-3 sm:px-3 sm:py-2 bg-secondary-800 border border-secondary-600 rounded-xl sm:rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 text-base"
                            />
                        </div>
                    </div>

                    {/* Game Selection (Chips) */}
                    <div>
                        <label className="block text-base sm:text-sm font-medium text-secondary-300 mb-2">
                            Select Game / Format
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {GAMES.map(g => (
                                <button
                                    key={g.id}
                                    type="button"
                                    onClick={() => setGame(g.id)}
                                    className={`
                                        relative px-3 py-3 rounded-xl border text-sm font-medium transition-all
                                        ${game === g.id
                                            ? 'bg-primary-500/20 border-primary-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                                            : 'bg-secondary-800 border-secondary-600 text-secondary-400 hover:bg-secondary-750 hover:border-secondary-500'}
                                    `}
                                >
                                    {game === g.id && (
                                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                                    )}
                                    {g.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Strategies (Required) */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Strategies Observed *
                        </label>
                        <textarea
                            value={strategies}
                            onChange={(e) => setStrategies(e.target.value)}
                            placeholder="What strategies did they use? What patterns did you notice?"
                            rows={3}
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 resize-none"
                            required
                        />
                    </div>

                    {/* Strengths & Weaknesses */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-green-400 mb-1">
                                💪 Strengths
                            </label>
                            <textarea
                                value={strengths}
                                onChange={(e) => setStrengths(e.target.value)}
                                placeholder="What are they good at?"
                                rows={2}
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-green-500 resize-none"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-red-400 mb-1">
                                🎯 Weaknesses
                            </label>
                            <textarea
                                value={weaknesses}
                                onChange={(e) => setWeaknesses(e.target.value)}
                                placeholder="What can we exploit?"
                                rows={2}
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-red-500 resize-none"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Additional Notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any other observations..."
                            rows={2}
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 resize-none"
                        />
                    </div>

                    {/* Player Names */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Player Names (comma-separated)
                        </label>
                        <input
                            type="text"
                            value={playerNames}
                            onChange={(e) => setPlayerNames(e.target.value)}
                            placeholder="Player1, Player2, Player3"
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500"
                        />
                    </div>

                    {/* VoD URL and Tags */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-1">
                                📹 VoD Link
                            </label>
                            <input
                                type="url"
                                value={vodUrl}
                                onChange={(e) => setVodUrl(e.target.value)}
                                placeholder="https://youtube.com/..."
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-1">
                                ⚔️ Start.gg Link
                            </label>
                            <input
                                type="url"
                                value={startGGMatchUrl}
                                onChange={(e) => setStartGGMatchUrl(e.target.value)}
                                placeholder="https://start.gg/..."
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-1">
                                🏷️ Tags (comma-separated)
                            </label>
                            <input
                                type="text"
                                value={tags}
                                onChange={(e) => setTags(e.target.value)}
                                placeholder="aggressive, campy, zoner"
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4 border-t border-secondary-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-secondary-800 text-secondary-300 rounded-lg hover:bg-secondary-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Submitting...
                                </span>
                            ) : (
                                '🔒 Submit Intel'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
